import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Modal, notification } from "antd";
import classNames from "classnames";
import { get, isEmpty, isNil, uniq } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import {
  MOCKUP_IMAGE_ERRORS,
  prepareMockupImage,
} from "utils/helpers/mockup-image";
import CanvasIcon from "./CanvasIcon";
import {
  CHAT_MODES,
  errorText,
  MESSAGE_ROLES,
  MESSAGE_STATUSES,
  proposalChanges,
  stateCount,
} from "./canvas-proposal";
import { useCanvasSelection } from "./canvas-selection";
import CitedChips from "./CitedChips";
import ClaudeProposal from "./ClaudeProposal";
import { ICON_PATHS } from "./step-look";
import { fillTemplate, WORKSHOP_ICONS } from "./workshop-view";

const POLL_MS = 2000;
const MAX_WAIT_MS = 15 * 60 * 1000;
const EXCERPT_SIZE = 60;

const ICONS = {
  history: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M12 7v5l3 2",
  send: "M12 19V5 M5 12l7-7 7 7",
};

const SILENT = { onError: () => undefined };

const excerpt = (text) => {
  const plain = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  return plain.length > EXCERPT_SIZE
    ? `${plain.slice(0, EXCERPT_SIZE)}…`
    : plain;
};

const pastedImageOf = (clipboard) => {
  if (isNil(clipboard)) return null;
  const fromFiles = Array.from(clipboard.files || []).find((file) =>
    String(file.type || "").startsWith("image/"),
  );
  if (fromFiles) return fromFiles;
  for (const item of Array.from(clipboard.items || [])) {
    if (
      item.kind === "file" &&
      String(item.type || "").startsWith("image/") &&
      typeof item.getAsFile === "function"
    )
      return item.getAsFile();
  }
  return null;
};

const AUTO_MODE = "auto";

const fullName = (user) =>
  [get(user, "firstname"), get(user, "lastname")].filter(Boolean).join(" ");

const ClaudePanel = ({ processId, nodes, edges, onApply, open, onClose }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const CLAUDE = t("CANVAS_EDITOR.CLAUDE", { returnObjects: true }) || {};
  const KINDS = t("CANVAS_EDITOR.CHIPS.KINDS", { returnObjects: true }) || {};
  const store = useContext(StoreContext);
  const { chips } = useCanvasSelection();
  const [draft, setDraft] = useState("");
  const [image, setImage] = useState(null);
  const [mode, setMode] = useState(null);
  const [sending, setSending] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [timedOut, setTimedOut] = useState([]);
  const threadRef = useRef(null);
  const fileRef = useRef(null);
  const where = useMemo(() => ({ process: { id: processId } }), [processId]);

  const { data, error, refetch } = useQuery(
    graphql.queries.CANVAS_CONVERSATION,
    {
      variables: { where },
      skip: isNil(processId),
      fetchPolicy: "cache-and-network",
      ...SILENT,
    },
  );
  const [fetchMessage] = useLazyQuery(graphql.queries.CANVAS_MESSAGE, {
    fetchPolicy: "network-only",
    ...SILENT,
  });
  const [sendMessage] = useMutation(
    graphql.mutations.SEND_CANVAS_MESSAGE,
    SILENT,
  );
  const [startConversation] = useMutation(
    graphql.mutations.NEW_CANVAS_CONVERSATION,
    SILENT,
  );
  const [refuseProposal] = useMutation(
    graphql.mutations.REFUSE_CANVAS_PROPOSAL,
    SILENT,
  );

  const messages = useMemo(
    () => get(data, "canvasConversation.messages") || [],
    [data],
  );
  const myId = get(store, "user.id") || null;
  const authorIds = useMemo(
    () =>
      uniq(
        messages
          .filter((message) => message.role === MESSAGE_ROLES.USER)
          .map((message) => message.authorId)
          .filter((id) => !isNil(id) && id !== myId),
      ),
    [messages, myId],
  );
  const { data: usersData } = useQuery(graphql.queries.USERS, {
    variables: { isIn: { id: authorIds } },
    skip: isEmpty(authorIds),
    ...SILENT,
  });
  const names = useMemo(
    () =>
      (get(usersData, "users.data") || []).reduce(
        (found, user) => ({ ...found, [user.id]: fullName(user) }),
        {},
      ),
    [usersData],
  );

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const fetchRef = useRef(fetchMessage);
  fetchRef.current = fetchMessage;
  const reload = () =>
    Promise.resolve(refetchRef.current ? refetchRef.current() : null).catch(
      () => null,
    );

  const pendingId =
    get(
      [...messages]
        .reverse()
        .find(
          (message) =>
            message.role === MESSAGE_ROLES.ASSISTANT &&
            message.status === MESSAGE_STATUSES.PENDING,
        ),
      "id",
    ) || null;
  const waiting = !isNil(pendingId) && !timedOut.includes(pendingId);

  useEffect(() => {
    if (!waiting) return undefined;
    let stopped = false;
    let timer = null;
    const startedAt = Date.now();
    const tick = async () => {
      const result = await Promise.resolve(
        fetchRef.current({ variables: { where: { id: pendingId } } }),
      ).catch(() => null);
      if (stopped) return;
      const status = get(result, "data.canvasMessage.status");
      if (!isNil(status) && status !== MESSAGE_STATUSES.PENDING) {
        Promise.resolve(refetchRef.current()).catch(() => null);
        return;
      }
      if (Date.now() - startedAt >= MAX_WAIT_MS) {
        setTimedOut((current) => [...current, pendingId]);
        return;
      }
      timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, POLL_MS);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [waiting, pendingId]);

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [messages.length, pendingId]);

  const busy = sending || waiting;
  const text = draft.trim();
  const canSend = text !== "" && !busy && !isNil(processId);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const result = await Promise.resolve(
      sendMessage({
        variables: {
          where,
          data: {
            text,
            refs: chips,
            ...(isNil(mode) ? {} : { mode }),
            ...(isNil(image) ? {} : { image: image.url }),
          },
        },
      }),
    ).catch(() => null);
    setSending(false);
    if (isNil(get(result, "data.sendCanvasMessage"))) {
      notification.error({
        message:
          get(CLAUDE.ERRORS, get(result, "errors.graphQLErrors.0.message")) ||
          CLAUDE.SEND_ERROR,
      });
      return;
    }
    setDraft("");
    setImage(null);
    await reload();
  };

  const attach = async (file) => {
    if (isNil(file)) return;
    try {
      const prepared = await prepareMockupImage(file);
      setImage({
        name: file.name || CLAUDE.ATTACH,
        url: `data:${prepared.mediaType};base64,${prepared.data}`,
      });
    } catch (failure) {
      notification.error({
        message:
          get(failure, "message") === MOCKUP_IMAGE_ERRORS.TOO_LARGE
            ? CLAUDE.IMAGE_TOO_LARGE
            : CLAUDE.IMAGE_UNREADABLE,
      });
    }
  };

  const startOver = () =>
    Modal.confirm({
      title: CLAUDE.NEW_TITLE,
      content: CLAUDE.NEW_CONTENT,
      okText: CLAUDE.NEW_OK,
      cancelText: CLAUDE.NEW_CANCEL,
      onOk: async () => {
        const result = await startConversation({ variables: { where } });
        if (isNil(get(result, "data.newCanvasConversation"))) {
          notification.error({ message: CLAUDE.NEW_ERROR });
          return;
        }
        setTimedOut([]);
        await reload();
      },
    });

  const decide = async (message, task) => {
    setDeciding({ id: message.id, task });
    try {
      if (task === "apply") {
        await onApply(message);
      } else {
        const result = await refuseProposal({
          variables: { where: { id: message.id } },
        });
        if (isNil(get(result, "data.refuseCanvasProposal")))
          notification.error({ message: CLAUDE.PROPOSAL.REFUSE_ERROR });
      }
      await reload();
    } finally {
      setDeciding(null);
    }
  };

  const authorOf = (message) => {
    if (isNil(message.authorId)) return CLAUDE.SOMEONE;
    if (message.authorId === myId) return CLAUDE.YOU;
    return names[message.authorId] || CLAUDE.SOMEONE;
  };

  const timeOf = (message) => {
    if (isNil(message.createdDate)) return "";
    const date = new Date(message.createdDate);
    return Number.isNaN(date.getTime())
      ? ""
      : date.toLocaleString(undefined, {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  const jumpTo = (id) => {
    const thread = threadRef.current;
    if (isNil(thread)) return;
    const target = thread.querySelector(`[data-message="${id}"]`);
    if (target && target.scrollIntoView)
      target.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const userMessages = messages.filter(
    (message) => message.role === MESSAGE_ROLES.USER,
  );
  const historyItems = userMessages.length
    ? [...userMessages].reverse().map((message) => ({
        key: message.id,
        label: (
          <span className="canvas-claude__history-item">
            <strong>
              {[authorOf(message), timeOf(message)].filter(Boolean).join(" · ")}
            </strong>
            <span>{excerpt(message.text)}</span>
          </span>
        ),
      }))
    : [{ key: "empty", label: CLAUDE.HISTORY_EMPTY, disabled: true }];

  const context = [
    t("CANVAS_EDITOR.CLAUDE.STEP_COUNT", { count: nodes.length }),
    t("CANVAS_EDITOR.CLAUDE.RULE_COUNT", { count: edges.length }),
    t("CANVAS_EDITOR.CLAUDE.STATE_COUNT", { count: stateCount(nodes) }),
  ]
    .filter(Boolean)
    .join(" · ");

  const renderUser = (message) => {
    const refs = Array.isArray(message.refs) ? message.refs : [];
    return (
      <div
        key={message.id}
        data-message={message.id}
        className="canvas-claude__message canvas-claude__message--user"
      >
        <span className="canvas-claude__author">
          {[authorOf(message), timeOf(message)].filter(Boolean).join(" · ")}
        </span>
        {refs.length ? (
          <div className="canvas-claude__refs">
            {refs.map((ref, index) => (
              <span
                key={`${ref.kind}:${ref.stepId}:${ref.stateId || ""}:${
                  ref.slotId || ""
                }:${index}`}
                className="canvas-claude__ref"
              >
                {[KINDS[ref.kind], ref.label].filter(Boolean).join(" · ")}
              </span>
            ))}
          </div>
        ) : null}
        <div className="canvas-claude__bubble">{message.text}</div>
      </div>
    );
  };

  const renderAssistant = (message) => {
    const pending = message.status === MESSAGE_STATUSES.PENDING;
    const failed = message.status === MESSAGE_STATUSES.FAILED;
    const slow = pending && timedOut.includes(message.id);
    return (
      <div
        key={message.id}
        data-message={message.id}
        className="canvas-claude__message canvas-claude__message--assistant"
      >
        <span className="canvas-claude__author canvas-claude__author--claude">
          {CLAUDE.TITLE}
        </span>
        {pending ? (
          <div className="canvas-claude__thinking" role="status">
            <CanvasIcon path={WORKSHOP_ICONS.busy} size={16} strokeWidth={2} />
            <span>{slow ? CLAUDE.SLOW : CLAUDE.THINKING}</span>
            {slow ? (
              <button
                type="button"
                className="canvas-claude__link"
                onClick={() =>
                  setTimedOut((current) =>
                    current.filter((id) => id !== message.id),
                  )
                }
              >
                {CLAUDE.CHECK_AGAIN}
              </button>
            ) : null}
          </div>
        ) : null}
        {failed ? (
          <div className="canvas-claude__failed" role="alert">
            <CanvasIcon path={WORKSHOP_ICONS.warn} size={16} strokeWidth={2} />
            <span>{errorText(message.error, CLAUDE.ERRORS)}</span>
          </div>
        ) : null}
        {message.text ? (
          <div className="canvas-claude__text">{message.text}</div>
        ) : null}
        {proposalChanges(message).length ? (
          <ClaudeProposal
            message={message}
            nodes={nodes}
            applying={
              get(deciding, "id") === message.id &&
              get(deciding, "task") === "apply"
            }
            refusing={
              get(deciding, "id") === message.id &&
              get(deciding, "task") === "refuse"
            }
            onApply={(item) => decide(item, "apply")}
            onRefuse={(item) => decide(item, "refuse")}
          />
        ) : null}
      </div>
    );
  };

  return (
    <aside
      className={classNames("canvas-claude", {
        "canvas-claude--hidden": !open,
      })}
      aria-label={CLAUDE.LABEL}
    >
      <div className="canvas-claude__head">
        <button
          type="button"
          className="canvas-claude__avatar canvas-claude__avatar--button"
          aria-label={CLAUDE.COLLAPSE}
          title={CLAUDE.COLLAPSE}
          aria-expanded={open}
          onClick={onClose}
        >
          <CanvasIcon path={ICON_PATHS.quote} size={18} strokeWidth={2} />
        </button>
        <div className="canvas-claude__heading">
          <h2 className="canvas-claude__title">{CLAUDE.TITLE}</h2>
          <span className="canvas-claude__subtitle">{CLAUDE.SUBTITLE}</span>
        </div>
        <Dropdown
          trigger={["click"]}
          menu={{
            items: historyItems,
            onClick: ({ key }) => jumpTo(key),
          }}
        >
          <button
            type="button"
            className="canvas-claude__tool"
            aria-label={CLAUDE.HISTORY}
            title={CLAUDE.HISTORY}
          >
            <CanvasIcon path={ICONS.history} size={18} strokeWidth={2} />
          </button>
        </Dropdown>
        <button
          type="button"
          className="canvas-claude__tool"
          aria-label={CLAUDE.NEW}
          title={CLAUDE.NEW}
          disabled={isNil(processId) || busy}
          onClick={startOver}
        >
          <CanvasIcon path={ICON_PATHS.plus} size={18} strokeWidth={2} />
        </button>
      </div>
      <div className="canvas-claude__context">
        <CanvasIcon path={WORKSHOP_ICONS.eye} size={16} strokeWidth={2} />
        <span>
          <strong>{CLAUDE.CONTEXT}</strong>
          {context ? ` · ${context}` : ""}
        </span>
      </div>
      <div className="canvas-claude__thread" ref={threadRef}>
        {error && !messages.length ? (
          <div className="canvas-claude__failed" role="alert">
            <CanvasIcon path={WORKSHOP_ICONS.warn} size={16} strokeWidth={2} />
            <span>{CLAUDE.ERRORS.DEFAULT}</span>
            <button
              type="button"
              className="canvas-claude__link"
              onClick={reload}
            >
              {CLAUDE.CHECK_AGAIN}
            </button>
          </div>
        ) : null}
        {!error && !messages.length ? (
          <div className="canvas-claude__empty">
            <strong>{CLAUDE.EMPTY_TITLE}</strong>
            <p>{CLAUDE.EMPTY_TEXT}</p>
          </div>
        ) : null}
        {messages.map((message) =>
          message.role === MESSAGE_ROLES.USER
            ? renderUser(message)
            : renderAssistant(message),
        )}
      </div>
      <div className="canvas-claude__foot">
        {chips.length ? <CitedChips /> : null}
        <div className="canvas-claude__box">
          {image ? (
            <span className="canvas-claude__image">
              <CanvasIcon path={WORKSHOP_ICONS.image} size={14} />
              <span className="canvas-claude__image-name">{image.name}</span>
              <button
                type="button"
                aria-label={fillTemplate(CLAUDE.REMOVE_IMAGE, {
                  name: image.name,
                })}
                onClick={() => setImage(null)}
              >
                <CanvasIcon
                  path={ICON_PATHS.close}
                  size={12}
                  strokeWidth={2.6}
                />
              </button>
            </span>
          ) : null}
          <textarea
            rows={3}
            aria-label={CLAUDE.INPUT}
            placeholder={CLAUDE.PLACEHOLDER}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onPaste={(event) => {
              const pasted = pastedImageOf(event.clipboardData);
              if (isNil(pasted)) return;
              event.preventDefault();
              attach(pasted);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || event.shiftKey) return;
              if (get(event, "nativeEvent.isComposing")) return;
              event.preventDefault();
              send();
            }}
          />
          <div className="canvas-claude__box-row">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(event) => {
                const file = event.target.files && event.target.files[0];
                event.target.value = "";
                attach(file);
              }}
            />
            <button
              type="button"
              className="canvas-claude__attach"
              aria-label={CLAUDE.ATTACH}
              title={CLAUDE.ATTACH}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              <CanvasIcon path={ICON_PATHS.plus} size={20} strokeWidth={1.8} />
            </button>
            <Dropdown
              trigger={["click"]}
              placement="topRight"
              menu={{
                selectable: true,
                selectedKeys: [mode || AUTO_MODE],
                items: [AUTO_MODE, CHAT_MODES.CODE, CHAT_MODES.ADMIN].map(
                  (item) => ({
                    key: item,
                    label: (
                      <span className="canvas-claude__mode-item">
                        <strong>{CLAUDE.MODE.NAMES[item]}</strong>
                        <span>{CLAUDE.MODE.HINTS[item]}</span>
                      </span>
                    ),
                  }),
                ),
                onClick: ({ key }) => setMode(key === AUTO_MODE ? null : key),
              }}
            >
              <button
                type="button"
                className="canvas-claude__mode"
                aria-label={CLAUDE.MODE.LABEL}
                title={CLAUDE.MODE.LABEL}
              >
                {CLAUDE.MODE.NAMES[mode || AUTO_MODE]}
                <CanvasIcon
                  path={ICON_PATHS.chevronDown}
                  size={14}
                  strokeWidth={2}
                />
              </button>
            </Dropdown>
            <button
              type="button"
              className={classNames("canvas-claude__send", {
                "canvas-claude__send--on": canSend,
              })}
              aria-label={CLAUDE.SEND}
              title={CLAUDE.SEND}
              disabled={!canSend}
              onClick={send}
            >
              <CanvasIcon path={ICONS.send} size={18} strokeWidth={2.2} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

ClaudePanel.propTypes = {
  processId: PropTypes.string,
  nodes: PropTypes.array,
  edges: PropTypes.array,
  onApply: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

ClaudePanel.defaultProps = {
  processId: null,
  nodes: [],
  edges: [],
  onApply: () => Promise.resolve(false),
  open: true,
  onClose: () => null,
};

export default ClaudePanel;
